import type { AlertDialogProps } from './Dialog.types';
import type { ReactElement } from 'react';

import { Flex, AlertDialog as RadixAlertDialog, ScrollArea } from '@rango-dev/whale';
import React, { useRef } from 'react';

import { getDialogMaxWidth } from './Dialog.helpers';
import styles from './Dialog.module.css';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Desktop branch of `AlertDialog` — a centered Radix Themes AlertDialog (used
 * as-is). Same chrome as the desktop Dialog but with no close button and no
 * outside/Esc dismissal; the footer action is required.
 */
export function DesktopAlertDialog(props: AlertDialogProps): ReactElement {
  const maxWidth = getDialogMaxWidth(props.width);
  const describedBy = props.description ? {} : { 'aria-describedby': undefined };
  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <RadixAlertDialog.Root
      open={props.open}
      defaultOpen={props.defaultOpen}
      onOpenChange={props.onOpenChange}>
      {props.trigger ? <RadixAlertDialog.Trigger>{props.trigger}</RadixAlertDialog.Trigger> : null}
      <RadixAlertDialog.Content
        ref={contentRef}
        maxWidth={maxWidth}
        className={styles.content}
        onOpenAutoFocus={(event) => {
          /*
           * Radix AlertDialog focuses its `AlertDialog.Cancel` on open; our
           * footer is generic, so focus the first control (the cancel action,
           * per footer order) to keep focus inside the dialog.
           */
          event.preventDefault();
          const target =
            contentRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ??
            contentRef.current;
          target?.focus();
        }}
        {...describedBy}>
        <Flex direction="column" gap="1">
          <RadixAlertDialog.Title mb="0" size="5" weight="bold">
            {props.title}
          </RadixAlertDialog.Title>
          {props.description ? (
            <RadixAlertDialog.Description mb="0" size="2" color="gray">
              {props.description}
            </RadixAlertDialog.Description>
          ) : null}
        </Flex>
        {props.children ? (
          <ScrollArea scrollbars="vertical" className={styles.body}>
            {props.children}
          </ScrollArea>
        ) : null}
        <Flex justify="end" gap="2" align="center">
          {props.footer}
        </Flex>
      </RadixAlertDialog.Content>
    </RadixAlertDialog.Root>
  );
}
